// Preset GLOBALI dei layout etichetta (default per formato, es. '50x30',
// '60x40'). Lettura: tabella public.label_presets (RLS: tutti gli autenticati).
// Scrittura: solo platform owner, via edge function `label-preset-save`
// (owner-gated). Le varianti PER RISTORANTE restano in label_templates.
// Vedi docs/superpowers/specs/2026-06-08-label-presets-design.md
import { getSupabase, SUPABASE_URL, SUPABASE_ANON_KEY } from './supabase.js';
import { dbg } from './debug/debugLog.js';

// Template trattato come JSON opaco: la lib lo trasporta, i chiamanti (editor /
// LabelsPage) hanno il tipo concreto LabelTemplate e fanno il cast.
export type PresetTemplate = Record<string, unknown>;
export type GlobalPresets = Record<string, PresetTemplate>;

const CACHE_KEY = 'trackeat.label_presets';

/** Chiave formato canonica da larghezza×altezza in mm. */
export function formatKey(widthMm: number, heightMm: number): string {
  return `${Math.round(widthMm)}x${Math.round(heightMm)}`;
}

export function cachedGlobalPresets(): GlobalPresets {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    return raw ? (JSON.parse(raw) as GlobalPresets) : {};
  } catch {
    return {};
  }
}

/**
 * Carica i preset globali dal DB (online) e li cacha. In caso di errore/offline
 * ritorna la cache locale: i preset cambiano di rado, non serve l'offline-first
 * rigoroso del sync, basta un fetch+cache con fallback.
 */
export async function loadGlobalPresets(): Promise<GlobalPresets> {
  try {
    const { data, error } = await getSupabase()
      .from('label_presets')
      .select('format, template');
    if (error) throw error;
    const map: GlobalPresets = {};
    for (const row of (data ?? []) as Array<{ format: string; template: PresetTemplate }>) {
      if (row.format && row.template) map[row.format] = row.template;
    }
    try {
      localStorage.setItem(CACHE_KEY, JSON.stringify(map));
    } catch {
      /* localStorage pieno: pazienza, resta in memoria per questa sessione */
    }
    return map;
  } catch (err) {
    dbg.warn('label-presets', 'load_failed', { error: err instanceof Error ? err.message : String(err) });
    return cachedGlobalPresets();
  }
}

/**
 * Salva un preset globale (solo platform owner). Chiama la edge function che
 * verifica l'identità owner e scrive con service_role. 403 → non sei l'owner.
 */
export async function saveGlobalPreset(format: string, template: PresetTemplate): Promise<void> {
  const { data } = await getSupabase().auth.getSession();
  const token = data.session?.access_token;
  if (!token) throw new Error('Sessione assente: rifai il login e riprova.');
  const res = await fetch(`${SUPABASE_URL.replace(/\/$/, '')}/functions/v1/label-preset-save`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      apikey: SUPABASE_ANON_KEY,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ format, template }),
  });
  if (!res.ok) {
    if (res.status === 403) {
      throw new Error('Solo il titolare della piattaforma può salvare i default globali.');
    }
    let detail = `HTTP ${res.status}`;
    try {
      const j = (await res.json()) as { error?: string };
      if (j.error) detail = j.error;
    } catch {
      /* body non-JSON */
    }
    throw new Error(`Salvataggio default globale fallito: ${detail}`);
  }
  // Invalida la cache così la prossima load rilegge il nuovo preset.
  try {
    localStorage.removeItem(CACHE_KEY);
  } catch {
    /* noop */
  }
}
